import {useEffect, useState} from 'react'
import {useFieldArray, useForm} from 'react-hook-form'
import {
  useRandomizeSalesPromoPrizeTransaction,
  useSalesPromoSelectPrizeTransaction,
  useSubmitSalesPromoPrizeTransaction,
} from 'src/app/modules/campaign/providers/SalesPromo.provider'
import {MallModel} from 'src/app/modules/directory/models'
import {
  SalesPromoSelectGiftModel,
  SalesPromoSelectGiftPrizesModel,
} from 'src/app/modules/campaign/models/SalesPromo.model'
import {safeArray} from 'src/app/utils/array-utils'
import {Refs} from 'src/app/utils/refs-utils'
import {getErrorMessage} from 'src/app/utils/api-utils'

export interface SelectGiftFormModel {
  directory?: MallModel
  claim_directory_id?: string
  prizes: SalesPromoSelectGiftPrizesModel[]
  direct_prize?: SalesPromoSelectGiftPrizesModel
  direct_prizes?: SalesPromoSelectGiftPrizesModel[]
  direct_prizes_quantity?: number
  extra_point_prize?: SalesPromoSelectGiftPrizesModel
}

const calculates = (sum: number, it: SalesPromoSelectGiftPrizesModel) => {
  const qunatityTimesGift = (it?.quantity || 0) * (it?.get_gift || 0)
  const availability = Math.min(it?.remaining_quantity || 0, it?.max_gift || 0)
  return sum + (Math.min(qunatityTimesGift, availability) ?? 0)
}
export function useSelectGiftByTransactionPopup(
  {
    campaign_id,
    member_id,
    member_sales_promo_id,
  }: {
    campaign_id?: string
    member_id?: string
    member_sales_promo_id?: string
  },
  clickCancel: () => void
) {
  const [directGiftOptions, setDirectGiftOptions] = useState<SalesPromoSelectGiftPrizesModel[]>()
  const [isRandomize, setIsRandomize] = useState<boolean>()
  const [defaultDirectGiftVal, setDefaultDirectGiftVal] =
    useState<SalesPromoSelectGiftPrizesModel[]>()

  const submit = useSubmitSalesPromoPrizeTransaction(campaign_id, member_id, member_sales_promo_id)
  const shuffle = useRandomizeSalesPromoPrizeTransaction(campaign_id, member_id)

  const selectGiftDefaultForm: SelectGiftFormModel = {
    claim_directory_id: undefined,
    directory: undefined,
    prizes: [],
    direct_prize: undefined,
    direct_prizes: [],
    direct_prizes_quantity: 0,
    extra_point_prize: undefined,
  }
  const selectGiftForm = useForm<SelectGiftFormModel>({
    defaultValues: selectGiftDefaultForm,
  })

  const {
    control: controlGift,
    setValue: setValueGift,
    getValues: getValuesGift,
    clearErrors: clearErrorsGift,
    reset: resetGift,
  } = selectGiftForm
  const {
    fields: fieldsGift,
    append: appendGift,
    remove: removeGift,
  } = useFieldArray({
    control: controlGift,
    name: `prizes`,
  })

  const exPointFieldsIdx = fieldsGift.findIndex((arg) => arg.type === 'EXTRA_POINT')

  const {
    control: controlDirectGift,
    setValue: setValueDirectGift,
    getValues: getValuesDirectGift,
    clearErrors: clearErrorsDirectGift,
    reset: resetDirectGift,
  } = selectGiftForm
  const {
    fields: fieldsDirectGift,
    append: appendDirectGift,
    remove: removeDirectGift,
  } = useFieldArray({
    control: controlDirectGift,
    name: `direct_prizes`,
  })

  const detail = useSalesPromoSelectPrizeTransaction(
    campaign_id,
    member_id,
    member_sales_promo_id,
    (data: SalesPromoSelectGiftModel) => {
      selectGiftForm.reset()
      selectGiftForm.setValue('directory', data.claim_directory)
      data.prizes?.forEach((element: SalesPromoSelectGiftPrizesModel) => {
        const {type, prizes, is_random} = element
        const newEl = {...element, quantity: 0}
        if (type === 'PRODUCT' && prizes) {
          selectGiftForm.setValue('direct_prize', element)
          const remakePrizes = prizes.map((it) => {
            return {...it, type: 'PRODUCT', quantity: 1}
          }) as SalesPromoSelectGiftPrizesModel[]
          if (is_random) {
            setIsRandomize(true)
            // calculates total quantity
            const total = Math.ceil(remakePrizes.length / (element?.get_gift || 1))
            selectGiftForm.setValue('direct_prizes_quantity', total)

            // fill initial value
            setDefaultDirectGiftVal(remakePrizes)
            // Increment multiplied by get_gift limited by max_gift
            const times = Math.min((element?.get_gift || 0) * total, element?.max_gift || 0)
            for (let i = 0; i < (times || 0); i++) {
              appendDirectGift(remakePrizes?.[i] || ({} as SalesPromoSelectGiftPrizesModel))
            }
          } else {
            setDirectGiftOptions(remakePrizes)
          }
        } else if (type === 'EXTRA_POINT') {
          selectGiftForm.setValue('extra_point_prize', newEl as SalesPromoSelectGiftPrizesModel)
        } else {
          appendGift(newEl as SalesPromoSelectGiftPrizesModel)
        }
      })
    },
    (error) => {
      Refs.pageToast.show({
        severity: 'danger',
        message: `${getErrorMessage(error, false)?.en}`,
      })
      clickCancel()
    }
  )

  const directGiftRule = selectGiftForm.watch('direct_prize')
  const valDirectPrizeQuantity = selectGiftForm.watch('direct_prizes_quantity')
  const valPrizes = selectGiftForm.watch('prizes')
  const valDirectGift = selectGiftForm.watch('direct_prizes')
  const valExtraPoint = selectGiftForm.watch('extra_point_prize')
  const valClaimLocation = selectGiftForm.watch('directory')?.id
  const hasDirectGift = detail?.data?.prizes?.some(
    (it: SalesPromoSelectGiftPrizesModel) => it.type === 'PRODUCT'
  )
  const lasItemRandomGift = valDirectGift?.at(-1)
  const disableIncreaseRandomGift =
    !lasItemRandomGift?.id && valDirectGift && valDirectGift.length > 0
  const totalDirectGift = valDirectGift?.filter((it) => it?.quantity)?.length || 0
  const totalPhysical = valPrizes
    ?.filter((el) => el.type === 'FISIK')
    .reduce((sum, it) => calculates(sum, it), 0)
  const totalDigital = valPrizes
    ?.filter((el) => el.type === 'DIGITAL')
    .reduce((sum, it) => calculates(sum, it), 0)
  const totalFreeParking = valPrizes
    ?.filter((el) => el.type === 'PARKING')
    .reduce((sum, it) => calculates(sum, it), 0)
  const totalValetParking = valPrizes
    ?.filter((el) => el.type === 'VALET')
    .reduce((sum, it) => calculates(sum, it), 0)
  const totalStamp = valPrizes
    ?.filter((el) => el.type === 'STAMP')
    .reduce((sum, it) => sum + (it.quantity ?? 0), 0)

  const summaryTable = [
    ...(hasDirectGift ? [{name: 'Direct Gift', quantity: totalDirectGift}] : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'DIGITAL')
      ? [{name: 'E-Voucher', quantity: totalDigital}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'PARKING')
      ? [{name: 'Free Parking', quantity: totalFreeParking}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'VALET')
      ? [{name: 'Valet Parking', quantity: totalValetParking}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'FISIK')
      ? [{name: 'Physical Voucher', quantity: totalPhysical}]
      : []),
    ...(detail?.data?.prizes?.some((it: SalesPromoSelectGiftPrizesModel) => it.type === 'STAMP')
      ? [{name: 'Stamp', quantity: totalStamp}]
      : []),
    ...(detail?.data?.prizes?.some(
      (it: SalesPromoSelectGiftPrizesModel) => it.type === 'EXTRA_POINT'
    )
      ? [{name: 'Point', quantity: valExtraPoint?.quantity || 0}]
      : []),
  ]

  const directGiftChange = ({type}: {type: 'inc' | 'dec'}) => {
    const totalItem = valDirectGift?.length || 0
    const lastIdx = totalItem - 1

    if (type === 'inc') {
      const remaining = (directGiftRule?.max_gift || 0) - (valDirectGift?.length || 0)
      const times = Math.min(directGiftRule?.get_gift || 0, remaining)
      // Increment multiplied by get_gift limited by max_gift
      for (let i = totalItem; i < totalItem + times; i++) {
        appendDirectGift(defaultDirectGiftVal?.[i] || ({} as SalesPromoSelectGiftPrizesModel))
      }
    } else {
      const remainder = totalItem % (directGiftRule?.get_gift || 0)
      const times = remainder > 0 ? remainder : directGiftRule?.get_gift || 0
      // Decrement by get_gift or modulo (see increment logic above)
      for (let i = lastIdx; i > lastIdx - times || 0; i--) {
        const target = valDirectGift?.[i]
        // Check if any option related to the selected item is already disabled
        const shouldEnable = directGiftOptions?.some(
          (el) => el?.id === target?.id && el?.isDisabled
        )
        if (!isRandomize && shouldEnable) {
          setDirectGiftOptions((prevVal?: SalesPromoSelectGiftPrizesModel[]) => {
            // Create new options, marking the matched ones with isDisabled: false
            const updatedOptions = safeArray(prevVal).map((item) => {
              if (item?.id === target?.id) {
                return {...item, isDisabled: false}
              }
              return item
            })
            return updatedOptions
          })
        }
        removeDirectGift(i)
      }
    }
  }

  const changeValueDirectPrize = (selected: SalesPromoSelectGiftPrizesModel, index: number) => {
    selectGiftForm.setValue(`direct_prizes.${index}`, selected)
    updateDisablingDirectGiftItem()
  }

  const updateDisablingDirectGiftItem = () => {
    setDirectGiftOptions((prevVal?: SalesPromoSelectGiftPrizesModel[]) => {
      const giftCounts: Record<string, number> = {}
      // Count how many times each id appears in valDirectGift
      safeArray(valDirectGift).forEach((item) => {
        if (item?.id) {
          giftCounts[item.id] = (giftCounts[item.id] || 0) + 1
        }
      })
      // Now update disabled state based on counts
      const updatedOptions = safeArray(prevVal).map((item) => {
        const count = giftCounts[item.id!] || 0
        const remaining = item.remaining_quantity || 1
        if (count >= remaining) {
          return {...item, isDisabled: true}
        }
        return {...item, isDisabled: false} // optional: reset disabled
      })
      return updatedOptions
    })
  }

  const reset = () => {
    selectGiftForm.reset()
    setDirectGiftOptions(undefined)
    setIsRandomize(undefined)
    setDefaultDirectGiftVal(undefined)
  }
  selectGiftForm.watch() // this is necessary to solve a common gotcha problem with react-hook-form and useFieldArray
  return {
    selectGiftForm,
    valClaimLocation,
    reset,
    detail,
    hasDirectGift,
    isRandomize,
    exPointFieldsIdx,
    gift: {
      fields: fieldsGift,
      append: appendGift,
      remove: removeGift,
    },
    directGift: {
      directGiftChange,
      directGiftOptions,
      setDirectGiftOptions,
      fields: fieldsDirectGift,
      append: appendDirectGift,
      remove: removeDirectGift,
      defaultDirectGiftVal,
      setDefaultDirectGiftVal,
      changeValueDirectPrize,
      shuffle,
      directGiftRule,
    },
    valDirectPrizeQuantity,
    valPrizes,
    valDirectGift,
    valExtraPoint,
    lasItemRandomGift,
    disableIncreaseRandomGift,
    summaryTable,
    submit,
  }
}
